import { gql } from "@apollo/client";
import { RECEIPT_FIELDS, DOOR_COST_SNAPSHOT_FIELDS } from "./queries";

export const LOGIN_USER = gql`
  mutation loginUser($email: String!, $password: String!) {
    loginUser(email: $email, password: $password) {
      token
      user {
        _id
        firstName
        lastName
        email
        phoneNumberIds {
          phoneNumber
          numberType
          title
          textMessages {
            body
            sentReceivedTime
            outgoingBool
            twilioSID
            attachedImgs
          }
        }
        permission
        initLogin
        failedLogins
        streetAddress
        city
        state
        zipCode
        usesWebAuthn
      }
    }
  }
`;

export const ADD_USER = gql`
  mutation addUser($username: String!, $email: String!, $password: String!) {
    addUser(username: $username, email: $email, password: $password) {
      token
      user {
        _id
        username
      }
    }
  }
`;

export const SEND_MESSAGE = gql`
  mutation sendMessage(
    $nameInput: String!
    $emailInput: String!
    $cityInput: String!
    $phoneNumberInput: String!
    $numberType: String!
    $textConsent: Boolean!
    $intExtChoice: String!
    $discountChoice: String!
    $heardAboutUs: String
    $otherInput: String
    $imageIdArr: [String]
  ) {
    sendMessage(
      nameInput: $nameInput
      emailInput: $emailInput
      cityInput: $cityInput
      phoneNumberInput: $phoneNumberInput
      numberType: $numberType
      textConsent: $textConsent
      intExtChoice: $intExtChoice
      discountChoice: $discountChoice
      heardAboutUs: $heardAboutUs
      otherInput: $otherInput
      imageIdArr: $imageIdArr
    ) {
      message
      error
    }
  }
`;

export const NEW_CLIENT_PHOTO = gql`
  mutation storeImage(
    $imageData: String
    $beforeOrAfter: String
    $dateEntered: String
    $assignToClientId: String
    $note: String
  ) {
    storeImage(
      imageData: $imageData
      beforeOrAfter: $beforeOrAfter
      dateEntered: $dateEntered
      assignToClientId: $assignToClientId
      note: $note
    ) {
      _id
      dateEntered
    }
  }
`;

export const DELETE_BA_GROUP = gql`
  mutation deleteBAGroup($baGroupId: String!) {
    deleteBAGroup(BAGroupId: $baGroupId)
  }
`;

export const CREATE_BA_GROUP = gql`
  mutation createBAGroup(
    $beforeId: String!
    $afterId: String!
    $assignToClientId: String
    $weekGalleryDisplay: Boolean!
    $dateEntered: String
    $refinishers: [String]
    $stainColor: String
  ) {
    createBAGroup(
      beforeId: $beforeId
      afterId: $afterId
      assignToClientId: $assignToClientId
      weekGalleryDisplay: $weekGalleryDisplay
      dateEntered: $dateEntered
      refinishers: $refinishers
      stainColor: $stainColor
    ) {
      _id
      beforeId {
        _id
        imageData
        s3ImageUrl
        s3ThumbUrl
        beforeOrAfter
        dateEntered
      }
      afterId {
        _id
        imageData
        s3ImageUrl
        s3ThumbUrl
        beforeOrAfter
        dateEntered
      }
      weekGalleryDisplay
      dateEntered
      refinishers
      stainColor
    }
  }
`;

export const UPDATE_FINEST_JOBS = gql`
  mutation updateFinestJobs($BAGroupIdArr: [String]) {
    updateFinestJobs(BAGroupIdArr: $BAGroupIdArr) {
      _id
      BAGroupIds {
        _id
        beforeId {
          _id
          imageData
          s3ImageUrl
          s3ThumbUrl
          beforeOrAfter
          dateEntered
        }
        afterId {
          _id
          imageData
          s3ImageUrl
          s3ThumbUrl
          beforeOrAfter
          dateEntered
        }
        weekGalleryDisplay
        dateEntered
        stainColor
        refinishers
      }
    }
  }
`;

// Inventory hardening (2026-09-18): catalog mutations are ADMIN. Opening
// stock on add is written as a ledger row server-side. An inactive item with
// the same name is refused with extensions.code INACTIVE_ITEM_EXISTS
// (+ existingId) so the UI can offer Restore.
export const ADD_SHOP_ITEM = gql`
  mutation addShopInventoryItem(
    $itemName: String!
    $currentQuantity: String!
    $maxQuantity: String!
    $price: String!
    $defaultTechQty: String
    $stockUnit: String
    $piecesPerUnit: String
    $pieceName: String
  ) {
    addInventoryItem(
      itemName: $itemName
      currentQuantity: $currentQuantity
      maxQuantity: $maxQuantity
      price: $price
      defaultTechQty: $defaultTechQty
      stockUnit: $stockUnit
      piecesPerUnit: $piecesPerUnit
      pieceName: $pieceName
    ) {
      id
      itemName
      currentQuantity
      maxQuantity
      price
      defaultTechQty
      stockUnit
      piecesPerUnit
      pieceName
      active
      deletedAt
    }
  }
`;

// Quantity is NOT editable here any more — a changed value is refused by the
// server (VALIDATION_FAILED); use ADJUST_SHOP_ITEM so the change lands in the
// ledger with a reason. Contents (piecesPerUnit + pieceName): omitted = left
// as is, piecesPerUnit "" = clear both.
export const UPDATE_SHOP_ITEM = gql`
  mutation UpdateShopItem(
    $itemId: String!
    $maxQuantity: String!
    $price: String!
    $defaultTechQty: String
    $stockUnit: String
    $piecesPerUnit: String
    $pieceName: String
  ) {
    updateInventoryItem(
      itemId: $itemId
      maxQuantity: $maxQuantity
      price: $price
      defaultTechQty: $defaultTechQty
      stockUnit: $stockUnit
      piecesPerUnit: $piecesPerUnit
      pieceName: $pieceName
    ) {
      id
      itemName
      currentQuantity
      maxQuantity
      price
      defaultTechQty
      stockUnit
      piecesPerUnit
      pieceName
      active
      deletedAt
    }
  }
`;

// Soft delete (history kept; Restore available). Same Boolean contract.
export const DELETE_SHOP_ITEM = gql`
  mutation Mutation($itemId: String!) {
    deleteInventoryItem(itemId: $itemId)
  }
`;

export const RESTORE_SHOP_ITEM = gql`
  mutation RestoreShopItem($itemId: String!) {
    restoreInventoryItem(itemId: $itemId) {
      id
      itemName
      active
      deletedAt
    }
  }
`;

// Manual quantity change = a ledger row with a reason. Note required unless
// reason is `count`. idempotencyKey minted once per dialog.
export const ADJUST_SHOP_ITEM = gql`
  mutation AdjustShopItem($itemId: String!, $newQuantity: String!, $reason: String!, $note: String, $idempotencyKey: String!, $clientReportedAt: String) {
    adjustInventoryItem(itemId: $itemId, newQuantity: $newQuantity, reason: $reason, note: $note, idempotencyKey: $idempotencyKey, clientReportedAt: $clientReportedAt) {
      id
      itemName
      currentQuantity
    }
  }
`;

export const UPSERT_VENDOR_SKU = gql`
  mutation UpsertVendorSku($itemId: String!, $vendor: String!, $sku: String, $unitsPerPurchase: String!) {
    upsertVendorSku(itemId: $itemId, vendor: $vendor, sku: $sku, unitsPerPurchase: $unitsPerPurchase) {
      id
      vendorSkus {
        vendor
        sku
        unitsPerPurchase
        lastSeenAt
      }
    }
  }
`;

export const REMOVE_VENDOR_SKU = gql`
  mutation RemoveVendorSku($itemId: String!, $vendor: String!, $sku: String) {
    removeVendorSku(itemId: $itemId, vendor: $vendor, sku: $sku) {
      id
      vendorSkus {
        vendor
        sku
        unitsPerPurchase
        lastSeenAt
      }
    }
  }
`;

// Refinisher submit. Fixed 2026-09-18: the server returns Boolean, so the old
// selection set failed GraphQL validation on EVERY web submit; and the submit
// now sends markCompletedInvReq:false so the server stamps lastRequestedAt and
// pushes admins (it never did from the web before).
export const UPDATE_REFIN_INV = gql`
  mutation updatedUserInvReq($itemsIdAndCountArr: [RefinReqItems], $markCompletedInvReq: Boolean) {
    updateRefinInvReq(itemsIdAndCountArr: $itemsIdAndCountArr, markCompletedInvReq: $markCompletedInvReq)
  }
`;

export const CLEAR_REFIN_INV = gql`
  mutation clearRefinInvReq {
    clearRefinInvReq {
      firstName
      refinReqInv {
        reqItemId
        reqItemCount
      }
      _id
      lastName
      email
      permission
      phoneNumberIds {
        _id
        phoneNumber
        numberType
        title
      }
      initLogin
      failedLogins
      streetAddress
      city
      state
      zipCode
      active
      active
    }
  }
`;

export const UPDATE_USER_INFO = gql`
  mutation UpdateUser(
    $updateUserId: String!
    $firstName: String
    $lastName: String
    $email: String
    $phoneNumberIds: [PhoneNumUpdate]
    $streetAddress: String
    $city: String
    $state: String
    $zipCode: String
    $newPass: String
    $prevPass: String
    $initLogin: Boolean
  ) {
    updateUser(
      id: $updateUserId
      firstName: $firstName
      lastName: $lastName
      email: $email
      phoneNumberIds: $phoneNumberIds
      streetAddress: $streetAddress
      city: $city
      state: $state
      zipCode: $zipCode
      newPass: $newPass
      prevPass: $prevPass
      initLogin: $initLogin
    ) {
      token
      user {
        _id
        firstName
        lastName
        email
        phoneNumberIds {
          _id
          phoneNumber
          numberType
          title
          textMessages {
            body
            sentReceivedTime
            outgoingBool
            twilioSID
            attachedImgs
          }
        }
        permission
        initLogin
        failedLogins
        streetAddress
        city
        state
        zipCode
        refinReqInv {
          reqItemId
          reqItemCount
        }
        active
        usesWebAuthn
      }
    }
  }
`;

export const SEND_NEW_INVITE = gql`
  mutation SendInviteEmail(
    $emailToInvite: String
    $firstName: String
    $permission: String
  ) {
    sendInviteEmail(
      emailToInvite: $emailToInvite
      firstName: $firstName
      permission: $permission
    )
  }
`;

export const SEND_TEMP_PASS = gql`
  mutation Mutation($emailTo: String) {
    sendForgotPasswordEmail(emailTo: $emailTo)
  }
`;

// Token-based self-service reset. REQUEST returns a neutral Boolean (always
// true) so it can't enumerate accounts; REDEEM verifies the emailed code and
// returns an Auth token (logs the user straight in on success).
export const REQUEST_PASSWORD_RESET = gql`
  mutation requestPasswordReset($email: String!) {
    requestPasswordReset(email: $email)
  }
`;

export const REDEEM_PASSWORD_RESET = gql`
  mutation redeemPasswordReset($email: String!, $token: String!, $newPass: String!) {
    redeemPasswordReset(email: $email, token: $token, newPass: $newPass) {
      token
      user {
        _id
        permission
      }
    }
  }
`;

export const DEACTIVATE_USER = gql`
  mutation Mutation($idDelete: String!) {
    deactivateUser(idDelete: $idDelete)
  }
`;

export const ARCHIVE_EMAIL = gql`
  mutation Mutation($emailId: String!, $changeArchive: Boolean!) {
    archiveEmail(emailId: $emailId, changeArchive: $changeArchive)
  }
`;

// Mass inbox actions (2026-09-14). admin|office only server-side; each returns
// the number of sent ids the caller was actually allowed to touch. Max 200 per
// call — search mode loads the whole inbox, so callers chunk.
export const ARCHIVE_EMAILS = gql`
  mutation ArchiveEmails($emailIds: [String!]!, $changeArchive: Boolean!) {
    archiveEmails(emailIds: $emailIds, changeArchive: $changeArchive)
  }
`;

export const DELETE_EMAIL = gql`
  mutation Mutation($emailId: String!) {
    deleteEmailById(emailId: $emailId)
  }
`;

// Archived view, "whole thread" (Delete / Restore): only the confirmed ids still
// in the thread are touched. Both return how many were deleted / restored.
export const DELETE_EMAIL_THREAD = gql`
  mutation DeleteEmailThread($emailId: String!, $emailIds: [String!]!, $userEmailAddress: String, $showAll: Boolean) {
    deleteEmailThread(emailId: $emailId, emailIds: $emailIds, userEmailAddress: $userEmailAddress, showAll: $showAll)
  }
`;

export const RESTORE_EMAIL_THREAD = gql`
  mutation RestoreEmailThread($emailId: String!, $emailIds: [String!]!, $userEmailAddress: String, $showAll: Boolean) {
    restoreEmailThread(emailId: $emailId, emailIds: $emailIds, userEmailAddress: $userEmailAddress, showAll: $showAll)
  }
`;

export const NEW_VISIT = gql`
  mutation Mutation(
    $timeVisited: String!
    $signNum: String
    $pageVisited: String
  ) {
    newVisitor(
      timeVisited: $timeVisited
      signNum: $signNum
      pageVisited: $pageVisited
    ) {
      id
      ifQuote
      pagesVisited {
        home
        quote
        ourDoors
        about
        contact
      }
      timeVisited
      fromSign
    }
  }
`;

export const UPDATE_VISIT = gql`
  mutation Mutation(
    $updateVisitId: String
    $page: String
    $convertQuote: Boolean
  ) {
    updateVisit(id: $updateVisitId, page: $page, convertQuote: $convertQuote) {
      id
      timeVisited
      pagesVisited {
        home
        quote
        ourDoors
        about
        contact
      }
      ifQuote
      fromSign
    }
  }
`;

export const DELETE_VISIT = gql`
  mutation Mutation($deleteVisitId: String!) {
    deleteVisit(id: $deleteVisitId)
  }
`;

export const CREATE_SIGN_ASSIGN = gql`
  mutation CreateSignAssignment($signNum: String!, $signName: String!) {
    createSignAssignment(signNum: $signNum, signName: $signName) {
      id
      signNum
      signName
    }
  }
`;

export const UPDATE_SIGN_ASSIGN = gql`
  mutation UpdateSignAssignment($signNum: String!, $signName: String!) {
    updateSignAssignment(signNum: $signNum, signName: $signName) {
      id
      signNum
      signName
    }
  }
`;

export const DELETE_SIGN_ASSIGN = gql`
  mutation DeleteSignAssignment($id: String!) {
    deleteSignAssignment(id: $id)
  }
`;

export const SEND_EMAIL_TEMPLATE = gql`
  mutation SendEmailTemp(
    $to: String!
    $from: String!
    $subject: String!
    $text: String
    $type: String!
    $attachArr: [String]
    $quoteLink: [String]
    $invoiceLink: [String]
    $toFirstName: String
    $receivedPhoto: Boolean
  ) {
    sendEmailTemp(
      to: $to
      from: $from
      subject: $subject
      text: $text
      type: $type
      attachArr: $attachArr
      quoteLink: $quoteLink
      invoiceLink: $invoiceLink
      toFirstName: $toFirstName
      receivedPhoto: $receivedPhoto
    )
  }
`;

export const CREATE_NEW_CLIENT = gql`
  mutation CreateNewClient(
    $firstName: String!
    $lastName: String
    $primaryEmail: String
    $spouseName: String
    $phoneNumberIds: [PhoneNumUpdate]
    $secondaryEmail: String
    $streetAddress: String
    $city: String
    $state: String
    $zipCode: String
    $clientNotes: ClientNoteInput
    $gateCode: String
    $clientStatus: String
    $existingPhnId: String
    $referredBy: ReferredByInput
  ) {
    createNewClient(
      firstName: $firstName
      lastName: $lastName
      primaryEmail: $primaryEmail
      spouseName: $spouseName
      existingPhnId: $existingPhnId
      phoneNumberIds: $phoneNumberIds
      secondaryEmail: $secondaryEmail
      streetAddress: $streetAddress
      city: $city
      state: $state
      zipCode: $zipCode
      clientNotes: $clientNotes
      gateCode: $gateCode
      clientStatus: $clientStatus
      referredBy: $referredBy
    ) {
      _id
      firstName
      lastName
      spouseName
      phoneNumberIds {
        _id
        phoneNumber
        numberType
        title
        textMessages {
          body
          sentReceivedTime
          outgoingBool
          twilioSID
          attachedImgs
        }
      }
      primaryEmail
      secondaryEmail
      streetAddress
      city
      state
      zipCode
      gateCode
      clientNotes {
        _id
        note
        dateCreated
        dateUpdated
        updatedBy
        createdBy
      }
      clientStatus
      clientTasks {
        id
        assigneeName
        assigneeId
        taskType
        note
        associatedClientId
        associatedClientName
        active
        dateCreated
        dueDate
      }
    }
  }
`;

// Push 1 build 146 — narrow Client.referredBy update used by the
// JobFormReviewModal source-dropdown. Avoids the heavy UPDATE_CLIENT_INFO
// payload which requires firstName/lastName/primaryEmail.
export const UPDATE_CLIENT_REFERRED_BY = gql`
  mutation UpdateClientReferredBy(
    $clientId: String!
    $referredBy: ReferredByInput!
  ) {
    updateClientReferredBy(clientId: $clientId, referredBy: $referredBy)
  }
`;

// Developer-only escape hatch — destructive full-cascade clear of an
// accidental refinisher-referral assignment. Server enforces the developer
// role; UI only renders the button for developers AND only when the Phase 5
// Q1 lock has set in (referredByUserIdLockedByServer === true).
export const DEV_CLEAR_CLIENT_REFERRAL = gql`
  mutation DevClearClientReferral($clientId: String!, $jobFormId: String!) {
    devClearClientReferral(clientId: $clientId, jobFormId: $jobFormId)
  }
`;

// Developer-only hard-delete for an open (non-completed) referral
// submission. Deletes both the submission doc and the linked
// ClientDoorPhoto. Server enforces the developer role + non-completed
// gate; UI only renders the button on the Referrals page's detail
// panel when the selected submission is non-completed.
export const DEV_DELETE_REFERRAL_SUBMISSION = gql`
  mutation DevDeleteReferralSubmission($referralId: String!) {
    devDeleteReferralSubmission(referralId: $referralId)
  }
`;

// Referral system — admin Defaults Setup → Referral Fee tab. Single
// global default. Schema shape `{type, valueCents}` future-proofs for
// percentage; only "amount" UI ships now.
export const UPDATE_REFERRAL_SETTINGS = gql`
  mutation UpdateReferralSettings($referralFee: ReferralFeeInput!) {
    updateReferralSettings(referralFee: $referralFee) {
      _id
      referralFee {
        type
        valueCents
      }
      updatedAtMs
      updatedByUserId
    }
  }
`;

// Admin-only — set the manually-maintained Google review count + rating + link.
// Server rounds rating to one decimal and coerces count to a whole number.
export const SET_REVIEW_COUNTER = gql`
  mutation SetReviewCounter($reviewCount: String!, $rating: String!, $url: String) {
    setReviewCounter(reviewCount: $reviewCount, rating: $rating, url: $url) {
      _id
      reviewCount
      rating
      url
      updatedAtMs
      updatedByUserId
    }
  }
`;

// Referral system — admin marks a referral as resolved without
// linking to a customer (rare path; not the primary completion flow).
export const MARK_REFERRAL_COMPLETED = gql`
  mutation MarkReferralCompleted($referralId: String!, $linkedClientId: String!) {
    markReferralCompleted(referralId: $referralId, linkedClientId: $linkedClientId) {
      _id
      completed
      linkedClientId
      completedAtMs
    }
  }
`;

// Atomic 7-step create flow with E11000 retry recovery via the
// Client.createdFromReferralId unique sparse index. See REFERRAL_SYSTEM_PLAN.md.
export const CREATE_CLIENT_FROM_REFERRAL = gql`
  mutation CreateClientFromReferral(
    $referralId: String!
    $clientFields: ReferredClientInput!
    $attachPhoto: Boolean!
  ) {
    createClientFromReferral(
      referralId: $referralId
      clientFields: $clientFields
      attachPhoto: $attachPhoto
    ) {
      clientId
      referralId
      status
    }
  }
`;

// Routes through applyReferredByGuards — applies all 4 guards
// (self-referral block, Q1 overwrite, empty-string normalize,
// setByUserId stringification) parity with the other 3 writers.
export const ATTACH_REFERRAL_TO_EXISTING_CLIENT = gql`
  mutation AttachReferralToExistingClient(
    $referralId: String!
    $clientId: String!
    $attachPhoto: Boolean!
  ) {
    attachReferralToExistingClient(
      referralId: $referralId
      clientId: $clientId
      attachPhoto: $attachPhoto
    )
  }
`;

export const UPDATE_CLIENT_INFO = gql`
  mutation UpdateClient(
    $clientId: String!
    $firstName: String!
    $lastName: String!
    $primaryEmail: String!
    $spouseName: String
    $phoneNumberIds: [PhoneNumUpdate]
    $secondaryEmail: String
    $streetAddress: String
    $city: String
    $state: String
    $zipCode: String
    $gateCode: String
    $clientNotes: ClientNoteInput
    $clientStatus: String
    $referredBy: ReferredByInput
  ) {
    updateClient(
      clientId: $clientId
      firstName: $firstName
      lastName: $lastName
      primaryEmail: $primaryEmail
      spouseName: $spouseName
      phoneNumberIds: $phoneNumberIds
      secondaryEmail: $secondaryEmail
      streetAddress: $streetAddress
      city: $city
      state: $state
      zipCode: $zipCode
      gateCode: $gateCode
      clientNotes: $clientNotes
      clientStatus: $clientStatus
      referredBy: $referredBy
    ) {
      _id
      firstName
      lastName
      spouseName
      phoneNumberIds {
        _id
        phoneNumber
        numberType
        title
        textMessages {
          body
          sentReceivedTime
          outgoingBool
          twilioSID
          attachedImgs
        }
      }
      primaryEmail
      secondaryEmail
      streetAddress
      city
      state
      zipCode
      gateCode
      clientNotes {
        _id
        note
        dateCreated
        dateUpdated
        updatedBy
        createdBy
      }
      clientStatus
      clientTasks {
        id
        assigneeName
        assigneeId
        taskType
        note
        associatedClientId
        associatedClientName
        active
        dateCreated
        dueDate
      }
      keapClientId
      keapClientMergeErrors
    }
  }
`;

export const ADD_CLIENT_NOTE = gql`
  mutation Mutation($clientId: String!, $noteObj: ClientNoteInput) {
    addClientNote(clientId: $clientId, noteObj: $noteObj)
  }
`;

export const DELETE_CLIENT_NOTE = gql`
  mutation DeleteClientNote($clientId: String!, $noteId: String!) {
    deleteClientNote(clientId: $clientId, noteId: $noteId)
  }
`;

export const UPDATE_CLIENT_NOTE = gql`
  mutation UpdateClientNote(
    $noteId: String!
    $noteText: String!
    $date: String!
    $userUpdated: String!
  ) {
    updateClientNote(
      noteId: $noteId
      noteText: $noteText
      date: $date
      userUpdated: $userUpdated
    )
  }
`;

export const DELETE_SERVICE_ITEM = gql`
  mutation DeleteServicesItem($itemId: String!) {
    deleteServicesItem(itemId: $itemId)
  }
`;

export const REORDER_SERVICE_ITEMS = gql`
  mutation ReorderServicesItems($orderedIds: [String!]!) {
    reorderServicesItems(orderedIds: $orderedIds) {
      id
      itemName
      order
    }
  }
`;

export const UPDATE_SERVICE_ITEM = gql`
  mutation UpdateServicesItem(
    $itemId: String!
    $itemName: String!
    $discountApplicable: Boolean!
    $price: String!
    $jobType: String
    $payBucket: String
    $customPayCents: String
    $requiresPhotos: Boolean
  ) {
    updateServicesItem(
      itemId: $itemId
      itemName: $itemName
      discountApplicable: $discountApplicable
      price: $price
      jobType: $jobType
      payBucket: $payBucket
      customPayCents: $customPayCents
      requiresPhotos: $requiresPhotos
    ) {
      id
      itemName
      discountApplicable
      price
      jobType
      payBucket
      customPayCents
      effectivePayBucket
      requiresPhotos
      order
      doorEquivalent
    }
  }
`;

export const ADD_SERVICE_ITEM = gql`
  mutation AddServicesItem(
    $itemName: String!
    $discountApplicable: Boolean!
    $price: String!
    $jobType: String
    $payBucket: String
    $customPayCents: String
    $requiresPhotos: Boolean
  ) {
    addServicesItem(
      itemName: $itemName
      discountApplicable: $discountApplicable
      price: $price
      jobType: $jobType
      payBucket: $payBucket
      customPayCents: $customPayCents
      requiresPhotos: $requiresPhotos
    ) {
      discountApplicable
      id
      itemName
      price
      jobType
      payBucket
      customPayCents
      effectivePayBucket
      requiresPhotos
      order
      doorEquivalent
    }
  }
`;

export const ADD_QUICK_TEXT = gql`
  mutation AddQuickText($label: String!, $value: String!, $active: Boolean) {
    addQuickText(label: $label, value: $value, active: $active) {
      id
      label
      value
      order
      active
    }
  }
`;

export const UPDATE_QUICK_TEXT = gql`
  mutation UpdateQuickText(
    $quickTextId: String!
    $label: String!
    $value: String!
    $active: Boolean
  ) {
    updateQuickText(
      quickTextId: $quickTextId
      label: $label
      value: $value
      active: $active
    ) {
      id
      label
      value
      order
      active
    }
  }
`;

export const DELETE_QUICK_TEXT = gql`
  mutation DeleteQuickText($quickTextId: String!) {
    deleteQuickText(quickTextId: $quickTextId)
  }
`;

export const REORDER_QUICK_TEXTS = gql`
  mutation ReorderQuickTexts($orderedIds: [String!]!) {
    reorderQuickTexts(orderedIds: $orderedIds) {
      id
      label
      order
    }
  }
`;

export const SET_QUICK_TEXT_OPENER = gql`
  mutation SetQuickTextOpener($openerLine: String!) {
    setQuickTextOpener(openerLine: $openerLine)
  }
`;

// Email templates Phase 1 (2026-09-16): Maintenance Program starting price
// printed in quote/invoice emails. Cents string.
export const SET_MAINT_PROGRAM_PRICE = gql`
  mutation SetMaintProgramPrice($cents: String!) {
    setMaintProgramPrice(cents: $cents) {
      maintProgramStartingCents
      maintProgramServicesItemId
      maintProgramServicesItemName
      maintProgramServicesItemPrice
    }
  }
`;

export const SET_MAINT_PROGRAM_LINK = gql`
  mutation SetMaintProgramLink($servicesItemId: String) {
    setMaintProgramLink(servicesItemId: $servicesItemId) {
      maintProgramStartingCents
      maintProgramServicesItemId
      maintProgramServicesItemName
      maintProgramServicesItemPrice
    }
  }
`;

// Email templates Phase 2 (2026-09-17): templates as editable data.
const EMAIL_TEMPLATE_RESULT = `
      key
      name
      builtin
      active
      editable
      deactivatable
      composerPickable
      materialized
      order
      linkKind
      askReceivedPhoto
      usedBy
      subjectDefault
      bodyDefault
      topHtml
      bottomHtml
      updatedAtMs
      updatedByName
      history {
        savedAtMs
        savedByName
        note
        snapshot {
          name
          subjectDefault
          bodyDefault
          topHtml
          bottomHtml
          linkKind
          askReceivedPhoto
          active
        }
      }
`;

export const UPDATE_EMAIL_TEMPLATE = gql`
  mutation UpdateEmailTemplate($key: String!, $input: EmailTemplateInput!, $note: String, $expectedUpdatedAtMs: String) {
    updateEmailTemplate(key: $key, input: $input, note: $note, expectedUpdatedAtMs: $expectedUpdatedAtMs) {
      ${EMAIL_TEMPLATE_RESULT}
    }
  }
`;

export const CREATE_EMAIL_TEMPLATE = gql`
  mutation CreateEmailTemplate($input: EmailTemplateInput!) {
    createEmailTemplate(input: $input) {
      ${EMAIL_TEMPLATE_RESULT}
    }
  }
`;

export const SET_EMAIL_TEMPLATE_ACTIVE = gql`
  mutation SetEmailTemplateActive($key: String!, $active: Boolean!) {
    setEmailTemplateActive(key: $key, active: $active) {
      ${EMAIL_TEMPLATE_RESULT}
    }
  }
`;

export const DELETE_EMAIL_TEMPLATE = gql`
  mutation DeleteEmailTemplate($key: String!) {
    deleteEmailTemplate(key: $key)
  }
`;

export const REORDER_EMAIL_TEMPLATES = gql`
  mutation ReorderEmailTemplates($orderedKeys: [String!]!) {
    reorderEmailTemplates(orderedKeys: $orderedKeys) {
      key
      order
    }
  }
`;

export const RESTORE_EMAIL_TEMPLATE_VERSION = gql`
  mutation RestoreEmailTemplateVersion($key: String!, $savedAtMs: String!) {
    restoreEmailTemplateVersion(key: $key, savedAtMs: $savedAtMs) {
      ${EMAIL_TEMPLATE_RESULT}
    }
  }
`;

export const RESET_EMAIL_TEMPLATE_TO_SHIPPED = gql`
  mutation ResetEmailTemplateToShipped($key: String!) {
    resetEmailTemplateToShipped(key: $key) {
      ${EMAIL_TEMPLATE_RESULT}
    }
  }
`;

export const SEND_EMAIL_TEMPLATE_TEST = gql`
  mutation SendEmailTemplateTest($key: String, $draft: EmailTemplateInput) {
    sendEmailTemplateTest(key: $key, draft: $draft)
  }
`;

export const ADD_BLOCKED_EMAIL = gql`
  mutation AddBlockedEmail($incEmail: String!) {
    addBlockedEmail(incEmail: $incEmail) {
      id
      email
      emailsReceived
    }
  }
`;

export const CREATE_CLIENT_QUOTE = gql`
  mutation CreateClientQuote(
    $quoteStatus: String!
    $quoteType: String
    $dateCreated: String!
    $createdBy: String!
    $quoteTotal: String!
    $subtotal: String!
    $customerName: String!
    $expirationDate: String!
    $discountPercent: String!
    $quoteItems: [QuoteItemsInput]
    $quoteNote: String!
    $customerId: String!
    $assigneeId: String
    $assigneeName: String
    $splitBetween: [Int]
    $specialRefinisherPay: SpecialRefinisherPayInput
  ) {
    createClientQuote(
      quoteStatus: $quoteStatus
      quoteType: $quoteType
      dateCreated: $dateCreated
      createdBy: $createdBy
      quoteTotal: $quoteTotal
      subtotal: $subtotal
      customerName: $customerName
      expirationDate: $expirationDate
      discountPercent: $discountPercent
      quoteItems: $quoteItems
      quoteNote: $quoteNote
      customerId: $customerId
      assigneeId: $assigneeId
      assigneeName: $assigneeName
      splitBetween: $splitBetween
      specialRefinisherPay: $specialRefinisherPay
    ) {
      id
      quoteStatus
      quoteType
      quoteViewed {
        timestamp
        isUser
      }
      dateCreated
      createdBy
      quoteItems {
        itemName
        price
        quantity
        discountApplicable
        jobType
        payBucket
        customPayCents
        requiresPhotos
      }
      quoteTotal
      subtotal
      customerName
      quoteNum
      expirationDate
      discountPercent
      quoteNote
      quoteAccepted
    }
  }
`;

export const MARK_EMAIL_UNREAD = gql`
  mutation MarkEmailUnread($emailId: String!, $read: Boolean!) {
    markEmailUnread(emailId: $emailId, read: $read)
  }
`;

export const REQ_WEB_AUTHN_CHALLENGE = gql`
  mutation ReqWebAuthnChallenge {
    reqWebAuthnChallenge {
      challenge
      ip
    }
  }
`;

export const DELETE_UNUSED_WEB_AUTHN_CHALLENGE = gql`
  mutation DeleteTempChallengeOnDenyWebAuthn($challenge: String!) {
    deleteTempChallengeOnDenyWebAuthn(challenge: $challenge)
  }
`;

export const CREATE_USER_TASK = gql`
  mutation Mutation(
    $assigneeName: String
    $assigneeId: String
    $taskType: String!
    $note: String
    $associatedClientName: String
    $associatedClientId: String
    $active: Boolean
    $dateCreated: String
    $dueDate: String
  ) {
    createUserTask(
      assigneeName: $assigneeName
      assigneeId: $assigneeId
      taskType: $taskType
      note: $note
      associatedClientName: $associatedClientName
      associatedClientId: $associatedClientId
      active: $active
      dateCreated: $dateCreated
      dueDate: $dueDate
    )
  }
`;

export const MARK_USER_TASK_COMPLETED = gql`
  mutation MarkCompletedTask($taskId: String!, $active: Boolean!) {
    markCompletedTask(taskId: $taskId, active: $active)
  }
`;

export const ACCEPT_CLIENT_QUOTE = gql`
  mutation AcceptClientQuote($quoteId: String!, $localEpoch: String!, $accessToken: String) {
    acceptClientQuote(quoteId: $quoteId, localEpoch: $localEpoch, accessToken: $accessToken)
  }
`;

export const CREATE_CLIENT_INVOICE = gql`
  mutation CreateClientInvoice(
    $quoteId: String!
    $localEpoch: String!
    $jobScheduledFor: String!
    $jobEndTime: String
    $invoicePaid: Boolean
    $invoiceNote: String
    $paidBy: String
    $skipCalendar: Boolean
    $specialRefinisherPay: SpecialRefinisherPayInput
  ) {
    createClientInvoice(
      quoteId: $quoteId
      localEpoch: $localEpoch
      jobScheduledFor: $jobScheduledFor
      jobEndTime: $jobEndTime
      invoicePaid: $invoicePaid
      invoiceNote: $invoiceNote
      paidBy: $paidBy
      skipCalendar: $skipCalendar
      specialRefinisherPay: $specialRefinisherPay
    ) {
      invoiceId
      relatedTaskId
      cyclePosition
      cycleConfidence
      cycleReason
    }
  }
`;

export const CREATE_PENDING_JOB_FORM = gql`
  mutation CreatePendingJobForm(
    $invoiceId: String!
    $clientId: String
  ) {
    createPendingJobForm(
      invoiceId: $invoiceId
      clientId: $clientId
    ) {
      _id
      status
    }
  }
`;

export const ARCHIVE_EMAIL_CLEANUP = gql`
  mutation ArchivedEmailCleanup($current: String!) {
    archivedEmailCleanup(current: $current)
  }
`;

export const MARK_EMAIL_FLAG = gql`
  mutation ChangeEmailFlag($emailId: String!, $flag: String!) {
    changeEmailFlag(emailId: $emailId, flag: $flag)
  }
`;

// Mass inbox actions — see ARCHIVE_EMAILS above.
export const MARK_EMAILS_READ = gql`
  mutation MarkEmailsRead($emailIds: [String!]!, $read: Boolean!) {
    markEmailsRead(emailIds: $emailIds, read: $read)
  }
`;

export const CHANGE_EMAILS_FLAG = gql`
  mutation ChangeEmailsFlag($emailIds: [String!]!, $flag: String!) {
    changeEmailsFlag(emailIds: $emailIds, flag: $flag)
  }
`;

export const SEND_SMS_TEXT = gql`
  mutation SendSMS(
    $textBody: String!
    $localTime: String!
    $phoneNumId: String!
    $sendToNum: String!
  ) {
    sendSMS(
      textBody: $textBody
      localTime: $localTime
      phoneNumId: $phoneNumId
      sendToNum: $sendToNum
    ) {
      _id
      numberType
      phoneNumber
      recentTime
      textConsent
      textMessages {
        _id
        body
        sentReceivedTime
        outgoingBool
        twilioSID
        attachedImgs
        status
        errorCode
        errorMessage
        statusUpdatedAt
      }
      title
    }
  }
`;

export const SEND_SMS_TEXT_STRIPPED_RETURN = gql`
  mutation SendSMS(
    $textBody: String!
    $localTime: String!
    $phoneNumId: String!
    $sendToNum: String!
  ) {
    sendSMS(
      textBody: $textBody
      localTime: $localTime
      phoneNumId: $phoneNumId
      sendToNum: $sendToNum
    ) {
      _id
      recentTime
      textMessages {
        _id
        body
        sentReceivedTime
        outgoingBool
        twilioSID
        attachedImgs
        status
        errorCode
        errorMessage
        statusUpdatedAt
      }
    }
  }
`;

export const ASSIGN_NUM_TO_CLIENT = gql`
  mutation AssignNumToClient(
    $clientFullName: String!
    $clientAlreadyHasId: Boolean!
    $localTime: String!
    $phoneNumId: String!
    $clientId: String!
    $move: Boolean
  ) {
    assignNumToClient(
      clientFullName: $clientFullName
      clientAlreadyHasId: $clientAlreadyHasId
      localTime: $localTime
      phoneNumId: $phoneNumId
      clientId: $clientId
      move: $move
    ) {
      _id
      phoneNumber
      numberType
      title
      recentTime
      unreadMessages
      hasTexts
      owners {
        ownerType
        ownerId
        firstName
        lastName
      }
      textMessages {
        body
        sentReceivedTime
        outgoingBool
        twilioSID
        attachedImgs
      }
    }
  }
`;

export const UPDATE_PHN_MSGS_READ = gql`
  mutation Mutation($phnId: String!, $localEpoch: String!) {
    updatePhnMsgsRead(phnId: $phnId, localEpoch: $localEpoch)
  }
`;

export const CREATE_PAYMENT_INTENT = gql`
  mutation CreatePaymentIntent($amountStr: String!, $testBool: Boolean) {
    createPaymentIntent(amountStr: $amountStr, testBool: $testBool)
  }
`;

export const UPDATE_PAYMENT_INTENT = gql`
  mutation UpdatePaymentIntent(
    $amountStr: String!
    $PIId: String!
    $tipStr: String
    $testBool: Boolean
  ) {
    updatePaymentIntent(
      amountStr: $amountStr
      PIId: $PIId
      tipStr: $tipStr
      testBool: $testBool
    )
  }
`;

export const CANCEL_PAYMENT_INTENT = gql`
  mutation CancelPaymentIntent(
    $amountStr: String
    $tipStr: String
    $newPiBool: Boolean!
    $piId: String!
    $testBool: Boolean
  ) {
    cancelPaymentIntent(
      amountStr: $amountStr
      tipStr: $tipStr
      newPIBool: $newPiBool
      PIId: $piId
      testBool: $testBool
    )
  }
`;

export const UPDATE_USER_TASK = gql`
  mutation UpdateUserTask(
    $id: String!
    $assigneeName: String!
    $assigneeId: String!
    $taskType: String!
    $note: String!
    $dueDate: String!
    $dateCreated: String
    $associatedClientId: String
    $associatedClientName: String
    $prevAssignedClient: Boolean!
  ) {
    updateUserTask(
      id: $id
      assigneeName: $assigneeName
      assigneeId: $assigneeId
      taskType: $taskType
      note: $note
      dueDate: $dueDate
      dateCreated: $dateCreated
      associatedClientId: $associatedClientId
      associatedClientName: $associatedClientName
      prevAssignedClient: $prevAssignedClient
    ) {
      id
      assigneeName
      assigneeId
      taskType
      note
      associatedClientId
      associatedClientName
      active
      dateCreated
      dueDate
    }
  }
`;

export const SAVE_STRIPE_TRANSACTION = gql`
  mutation SaveTransaction(
    $stripePIId: String!
    $amount: String!
    $tip: String!
    $total: String!
    $dateCreated: String!
    $invoiceId: String
    $subtotal: String
    $amountMatchesInvoice: Boolean
    $offsetMills: String
  ) {
    saveTransaction(
      stripePIId: $stripePIId
      amount: $amount
      tip: $tip
      total: $total
      dateCreated: $dateCreated
      invoiceId: $invoiceId
      subtotal: $subtotal
      amountMatchesInvoice: $amountMatchesInvoice
      offsetMills: $offsetMills
    ) {
      transactionSaved
      sentEmail
    }
  }
`;

export const LINK_STRIPE_TRANSACTION_TO_INVOICE = gql`
  mutation LinkStripeTransactionToInvoice(
    $stripeTransactionId: String!
    $invoiceNum: String!
  ) {
    linkStripeTransactionToInvoice(
      stripeTransactionId: $stripeTransactionId
      invoiceNum: $invoiceNum
    ) {
      success
      sentEmail
      receiptStatus
      alreadyPaid
      invoiceNum
      customerName
    }
  }
`;

export const KEAP_CLIENT_FETCH_AND_CREATE = gql`
  mutation KeapContactQuery($limit: String!, $offset: String!) {
    keapContactQuery(limit: $limit, offset: $offset) {
      errors
      more
    }
  }
`;

export const ASTERISK_CLEANUP = gql`
  mutation KeapContactQuery($noteArr: [asteriskUpdateInput]) {
    asteriskCleanup(noteArr: $noteArr)
  }
`;

export const CREATE_KEY_SHORTCUT = gql`
  mutation CreateKeyShortcut($title: String!, $text: String!) {
    createKeyShortcut(title: $title, text: $text) {
      id
      text
      title
    }
  }
`;

export const DELETE_KEY_SHORTCUT = gql`
  mutation DeleteKeyShortcut($id: String!) {
    deleteKeyShortcut(id: $id)
  }
`;

export const UPDATE_QUOTE_INVOICE = gql`
  mutation UpdateQuoteInvoice(
    $id: String!
    $isInvoice: Boolean!
    $discountPercent: String!
    $subtotal: String!
    $total: String!
    $dateMills: String
    $jobEndTime: String
    $itemsArr: [QuoteItemsInput]
    $note: String
    $splitBetween: [Int]
    $specialRefinisherPay: SpecialRefinisherPayInput
    $confirmPeriodMove: Boolean
  ) {
    updateQuoteInvoice(
      id: $id
      isInvoice: $isInvoice
      discountPercent: $discountPercent
      subtotal: $subtotal
      total: $total
      dateMills: $dateMills
      jobEndTime: $jobEndTime
      itemsArr: $itemsArr
      note: $note
      splitBetween: $splitBetween
      specialRefinisherPay: $specialRefinisherPay
      confirmPeriodMove: $confirmPeriodMove
    ) {
      invoice {
        id
        invoicePaid
        jobScheduledFor
        jobEndTime
        relatedTaskId
        dateCreated
        createdBy
        customerId
        invoiceNum
        quoteId
        invoiceNote
        paidBy
        invoiceItems {
          itemName
          price
          quantity
          discountApplicable
          jobType
          payBucket
          customPayCents
          requiresPhotos
        }
        invoiceTotal
        subtotal
        discountPercent
        customerName
        splitBetween
        specialRefinisherPay {
          enabled
          amount
          payType
        }
      }
      quote {
        id
        quoteStatus
        quoteAccepted
        quoteType
        quoteViewed {
          timestamp
          isUser
        }
        dateCreated
        createdBy
        quoteItems {
          itemName
          price
          quantity
          discountApplicable
          jobType
          payBucket
          customPayCents
          requiresPhotos
        }
        quoteTotal
        subtotal
        customerName
        quoteNum
        expirationDate
        discountPercent
        quoteNote
        customerId
        splitBetween
        specialRefinisherPay {
          enabled
          amount
          payType
        }
      }
    }
  }
`;

export const RETRIEVE_KEAP_FILE = gql`
  mutation retrieveKeapFile($keapFileId: String!) {
    retrieveKeapFile(keapFileId: $keapFileId)
  }
`;

export const MAKE_KEAP_FILE_MERGE_CALL = gql`
  mutation makeKeapFilesMergeCall($limit: String!, $offset: String!) {
    makeKeapFilesMergeCall(limit: $limit, offset: $offset)
  }
`;

// Opens a new TimeEntry with clockInMs = Date.now() and clockOutMs = null.
// Server refuses if an open shift already exists for this user.
export const CLOCK_IN_NOW = gql`
  mutation ClockInNow($userId: String!) {
    clockInNow(userId: $userId) {
      id
      userId
      clockInMs
      clockOutMs
      source
      createdByUserId
    }
  }
`;

// Closes the user's open TimeEntry by stamping clockOutMs = Date.now().
// Errors if no open shift exists.
export const CLOCK_OUT_NOW = gql`
  mutation ClockOutNow($userId: String!) {
    clockOutNow(userId: $userId) {
      id
      userId
      clockInMs
      clockOutMs
      source
      createdByUserId
    }
  }
`;

// Admin-only: insert a TimeEntry with both clockInMs and clockOutMs set.
// Used by the Timesheet review page to fill in a missed clock-in.
export const ADD_MANUAL_TIME_ENTRY = gql`
  mutation AddManualTimeEntry(
    $userId: String!
    $clockInMs: String!
    $clockOutMs: String!
  ) {
    addManualTimeEntry(
      userId: $userId
      clockInMs: $clockInMs
      clockOutMs: $clockOutMs
    ) {
      id
      userId
      clockInMs
      clockOutMs
      source
      createdByUserId
    }
  }
`;

// Admin-only: edit an existing TimeEntry. Server stamps editedAt and
// editedByUserId on every successful update. clockOutMs may be null to
// re-open the shift.
export const EDIT_TIME_ENTRY = gql`
  mutation EditTimeEntry(
    $entryId: String!
    $clockInMs: String!
    $clockOutMs: String
  ) {
    editTimeEntry(
      entryId: $entryId
      clockInMs: $clockInMs
      clockOutMs: $clockOutMs
    ) {
      id
      userId
      clockInMs
      clockOutMs
      source
      createdByUserId
      editedAt
      editedByUserId
    }
  }
`;

// Admin-only: hard-delete a TimeEntry. Single-user / owner's-daughter
// audit needs are minimal per Q5=B; no soft delete.
export const DELETE_TIME_ENTRY = gql`
  mutation DeleteTimeEntry($entryId: String!) {
    deleteTimeEntry(entryId: $entryId)
  }
`;

export const UPDATE_USER_WAGE = gql`
  mutation EditUserWage($userId: String!, $wage: String!) {
    editUserWage(userId: $userId, wage: $wage)
  }
`;

export const UPDATE_USER_LEGAL_NAME = gql`
  mutation UpdateUserLegalName(
    $userId: String!
    $legalFirstName: String!
    $legalLastName: String!
  ) {
    updateUserLegalName(
      userId: $userId
      legalFirstName: $legalFirstName
      legalLastName: $legalLastName
    )
  }
`;

export const UPDATE_USER_SENIORITY_PAY = gql`
  mutation UpdateUserSeniorityPay(
    $userId: String!
    $seniority: String
    $payPercentage: String
  ) {
    updateUserSeniorityPay(
      userId: $userId
      seniority: $seniority
      payPercentage: $payPercentage
    )
  }
`;

export const UPDATE_USER_DEFAULT_COLOR = gql`
  mutation UpdateUserDefaultColor($userId: String!, $defaultColor: String!) {
    updateUserDefaultColor(userId: $userId, defaultColor: $defaultColor)
  }
`;

export const SEND_MAINT_REM = gql`
  mutation SendMaintenanceReminders(
    $currentDate: String!
    $offsetMills: String!
  ) {
    sendMaintenanceReminders(
      currentDate: $currentDate
      offsetMills: $offsetMills
    )
  }
`;

export const CONFIRM_MAINT_CYCLE = gql`
  mutation ConfirmMaintenanceCycle($taskId: ID!, $cyclePosition: Int!) {
    confirmMaintenanceCycle(taskId: $taskId, cyclePosition: $cyclePosition) {
      id
      cyclePositionAtCreate
      note
      assigneeId
      taskType
      active
      dueDate
    }
  }
`;

export const SEND_ONE_MAINT_REMINDER = gql`
  mutation SendOneMaintenanceReminder(
    $taskId: ID!
    $currentDate: String!
    $offsetMills: String!
  ) {
    sendOneMaintenanceReminder(
      taskId: $taskId
      currentDate: $currentDate
      offsetMills: $offsetMills
    )
  }
`;

export const DELETE_CLIENT = gql`
  mutation Mutation($clientId: String!) {
    deleteClient(clientId: $clientId)
  }
`;

// Manager Linking — set/replace a child client's managedById link. Server
// runs cycle prevention; throws on cycle. Returns the updated child with
// `manager` + `managedClientsCount` populated for an immediate UI refresh.
export const LINK_CLIENT_MANAGER = gql`
  mutation LinkClientManager($clientId: String!, $managerId: String!) {
    linkClientManager(clientId: $clientId, managerId: $managerId) {
      _id
      managedById
      manager {
        _id
        firstName
        lastName
      }
      managedClientsCount
    }
  }
`;

// Manager Linking — clear a client's managedById. Returns the updated
// client with `manager` set to null and a fresh managedClientsCount.
export const UNLINK_CLIENT_MANAGER = gql`
  mutation UnlinkClientManager($clientId: String!) {
    unlinkClientManager(clientId: $clientId) {
      _id
      managedById
      manager {
        _id
        firstName
        lastName
      }
      managedClientsCount
    }
  }
`;

export const UPDATE_IMG_NOTE = gql`
  mutation Mutation($imgId: String!, $note: String) {
    updateImgNote(imgId: $imgId, note: $note)
  }
`;

export const DELETE_CLIENT_DOOR_PHOTO = gql`
  mutation DeleteClientDoorPhoto($photoId: ID!, $clientId: ID!) {
    deleteClientDoorPhoto(photoId: $photoId, clientId: $clientId) {
      ok
      reason
    }
  }
`;

export const CREATE_CALENDAR_ITEM = gql`
  mutation CreateCalendarItem($input: CalendarItemInput!) {
    createCalendarItem(input: $input) {
      id
      invoiceId
      quoteId
      clientId
      startMs
      endMs
      workDays {
        startMs
        endMs
      }
      priorityNotes
      assignedUserIds
      itemStatus
      isPlaceholder
      placeholderSplitBetween
      eventType
      chainGroupId
      note
      photoIds
      label
      isAllDay
      specialRefinisherPay {
        enabled
        amount
        payType
      }
    }
  }
`;

export const UPDATE_CALENDAR_ITEM = gql`
  mutation UpdateCalendarItem($input: UpdateCalendarItemInput!) {
    updateCalendarItem(input: $input) {
      id
      invoiceId
      quoteId
      clientId
      startMs
      endMs
      workDays {
        startMs
        endMs
      }
      priorityNotes
      assignedUserIds
      itemStatus
      isPlaceholder
      placeholderSplitBetween
      eventType
      chainGroupId
      note
      photoIds
      label
      isAllDay
      specialRefinisherPay {
        enabled
        amount
        payType
      }
    }
  }
`;

// scope (2026-09-20, recurring personal events): "occurrence" | "following" |
// "series". Omit it for a one-off. For an OCCURRENCE id the server defaults to
// "occurrence" when it is absent, so an older client can never wipe a series
// from a dialog that says one event is being removed — but send it explicitly
// whenever the user was offered the choice.
export const DELETE_CALENDAR_ITEM = gql`
  mutation DeleteCalendarItem($id: String!, $scope: String) {
    deleteCalendarItem(id: $id, scope: $scope)
  }
`;

// Batch-clear tentative placeholders (cleanup modal). Server re-validates each
// id is a true placeholder for $clientId before deleting; returns the count
// actually removed.
export const DELETE_CALENDAR_ITEMS = gql`
  mutation DeleteCalendarItems($ids: [String!]!, $clientId: String!) {
    deleteCalendarItems(ids: $ids, clientId: $clientId)
  }
`;

// Promote a tentative placeholder onto an invoice/quote. If that doc already
// has a calendar event, the server (with confirmDocMove:true) moves that event
// to the placeholder's slot and deletes the placeholder. Else it attaches the
// doc to the placeholder in place. Returns the surviving event.
export const PROMOTE_PLACEHOLDER_TO_DOC = gql`
  mutation PromotePlaceholderToDoc($placeholderId: String!, $invoiceId: String, $quoteId: String, $confirmDocMove: Boolean, $confirmPeriodMove: Boolean) {
    promotePlaceholderToDoc(placeholderId: $placeholderId, invoiceId: $invoiceId, quoteId: $quoteId, confirmDocMove: $confirmDocMove, confirmPeriodMove: $confirmPeriodMove) {
      id
      startMs
      endMs
      invoiceId
      quoteId
      isPlaceholder
      eventType
    }
  }
`;

export const BREAK_CALENDAR_CHAIN = gql`
  mutation BreakCalendarChain($id: String!) {
    breakCalendarChain(id: $id) {
      id
      chainGroupId
    }
  }
`;

export const JOIN_CALENDAR_CHAIN = gql`
  mutation JoinCalendarChain($sourceId: String!, $targetId: String!) {
    joinCalendarChain(sourceId: $sourceId, targetId: $targetId) {
      id
      chainGroupId
    }
  }
`;

// Live-updates Phase 2: response now includes `version` so callers can
// track it for the next save's `expectedVersion`. The expectedVersion
// itself is part of $input (JobFormInput.expectedVersion: Int) — it
// doesn't need a separate mutation arg.
export const CREATE_OR_UPDATE_JOB_FORM = gql`
  mutation CreateOrUpdateJobForm($input: JobFormInput!) {
    createOrUpdateJobForm(input: $input) {
      _id
      invoiceId
      status
      dateUpdated
      version
    }
  }
`;

export const UPDATE_BAGROUP_WEEK_DISPLAY = gql`
  mutation UpdateBAGroupWeekDisplay($bagroupId: String!, $weekGalleryDisplay: Boolean!) {
    updateBAGroupWeekDisplay(bagroupId: $bagroupId, weekGalleryDisplay: $weekGalleryDisplay) {
      _id
      weekGalleryDisplay
    }
  }
`;

// Dev tool: set a BAGroup's before/after photo ref to a different
// ClientDoorPhoto _id. Server accepts both as optional so callers pass only
// the field they want to replace. Used by the JFR developer photo picker.
export const UPDATE_JOB_FORM_BAGROUP = gql`
  mutation UpdateJobFormBAGroup($bagroupId: String!, $beforeId: String, $afterId: String) {
    updateJobFormBAGroup(bagroupId: $bagroupId, beforeId: $beforeId, afterId: $afterId) {
      _id
    }
  }
`;

// Dev tool: delete a non-approved JobForm, its BAGroup join records, and its
// linked LedgerItem (if any). Photos, invoice, client note, and calendar
// item are preserved. Resolver blocks on approved/completed status.
export const ADMIN_DELETE_JOB_FORM = gql`
  mutation AdminDeleteJobForm($jobFormId: String!) {
    adminDeleteJobForm(jobFormId: $jobFormId)
  }
`;

// Tokenized public share URL for admin copy-link buttons. Server guarantees
// an accessToken exists on the doc (creates one if missing via the central
// ensureAccessToken util), then returns the full URL with ?token=... so the
// customer's view won't fail on the resolver's token check. docType must be
// "quote" or "invoice".
export const GET_PUBLIC_SHARE_URL = gql`
  mutation GetPublicShareUrl($docType: String!, $docId: String!) {
    getPublicShareUrl(docType: $docType, docId: $docId)
  }
`;

// Live-updates Phase 2: accept optional `expectedVersion`; response carries
// `version` so callers can track it across status changes (e.g. approve →
// request changes round-trip). When expectedVersion mismatches the server's
// current version, the resolver throws STALE_VERSION (extension carries
// currentVersion) so the caller can refetch and re-apply.
export const UPDATE_JOB_FORM_STATUS = gql`
  mutation UpdateJobFormStatus($jobFormId: String!, $status: String!, $expectedVersion: Int) {
    updateJobFormStatus(jobFormId: $jobFormId, status: $status, expectedVersion: $expectedVersion) {
      _id
      status
      reviewedBy
      dateReviewed
      version
    }
  }
`;

export const ADMIN_RESOLVE_STUCK_SUBMISSION = gql`
  mutation AdminResolveStuckSubmission($id: ID!, $kind: String!, $note: String) {
    adminResolveStuckSubmission(id: $id, kind: $kind, note: $note) {
      _id
      resolvedAt
      resolutionKind
      resolutionNote
    }
  }
`;

export const ADMIN_FORCE_SUBMIT_JOB_FORM = gql`
  mutation AdminForceSubmitJobForm($stuckSubmissionId: ID!, $note: String) {
    adminForceSubmitJobForm(stuckSubmissionId: $stuckSubmissionId, note: $note) {
      _id
      status
      dateSubmitted
    }
  }
`;

export const CREATE_JOB_OPTION = gql`
  mutation CreateJobOption($title: String!, $listType: String!) {
    createJobOption(title: $title, listType: $listType) {
      _id
      title
      listType
      popularityGrade
    }
  }
`;

export const UPDATE_JOB_OPTION = gql`
  mutation UpdateJobOption($optionId: String!, $title: String, $popularityGrade: Int) {
    updateJobOption(optionId: $optionId, title: $title, popularityGrade: $popularityGrade) {
      _id
      title
      listType
      popularityGrade
    }
  }
`;

export const DELETE_JOB_OPTION = gql`
  mutation DeleteJobOption($id: String!) {
    deleteJobOption(id: $id)
  }
`;

export const GENERATE_ASSIGNMENT_SUGGESTIONS = gql`
  mutation GenerateAssignmentSuggestions($input: AssignmentRequestInput!) {
    generateAssignmentSuggestions(input: $input) {
      days {
        date
        assignments {
          calendarItemId
          assignedRefinisherIds
          splitCount
          jobAddress
          customerName
          locked
        }
      }
      refinisherSummaries {
        refinisherId
        refinisherName
        seniority
        payPercentage
        weeklyNetPay
        dailyBreakdown {
          date
          netPay
          jobCount
          totalDriveMinutes
        }
      }
      warnings
    }
  }
`;

export const ACCEPT_DAY_ASSIGNMENTS = gql`
  mutation AcceptDayAssignments($input: AcceptDayAssignmentsInput!) {
    acceptDayAssignments(input: $input)
  }
`;

export const CREATE_REFERRED_BY_OPTION = gql`
  mutation CreateReferredByOption($source: String!) {
    createReferredByOption(source: $source) {
      _id
      source
    }
  }
`;

export const UPDATE_REFERRED_BY_OPTION = gql`
  mutation UpdateReferredByOption($optionId: String!, $source: String!) {
    updateReferredByOption(optionId: $optionId, source: $source) {
      _id
      source
    }
  }
`;

export const DELETE_REFERRED_BY_OPTION = gql`
  mutation DeleteReferredByOption($optionId: String!) {
    deleteReferredByOption(optionId: $optionId) {
      _id
    }
  }
`;

export const CREATE_HARDWARE_OPERATION_OPTION = gql`
  mutation CreateHardwareOperationOption($optionName: String!) {
    createHardwareOperationOption(optionName: $optionName) {
      _id
      optionName
    }
  }
`;

export const UPDATE_HARDWARE_OPERATION_OPTION = gql`
  mutation UpdateHardwareOperationOption($optionId: String!, $optionName: String!) {
    updateHardwareOperationOption(optionId: $optionId, optionName: $optionName) {
      _id
      optionName
    }
  }
`;

export const DELETE_HARDWARE_OPERATION_OPTION = gql`
  mutation DeleteHardwareOperationOption($optionId: String!) {
    deleteHardwareOperationOption(optionId: $optionId)
  }
`;

export const ADMIN_REMOVE_FCM_TOKEN = gql`
  mutation AdminRemoveFcmToken($userId: String!, $deviceId: String!) {
    adminRemoveFcmToken(userId: $userId, deviceId: $deviceId)
  }
`;

// Fix B (2026-05-11) — Payroll Advances mutations. All admin-gated server-side.
export const CREATE_PAYROLL_ADVANCE = gql`
  mutation CreatePayrollAdvance($input: PayrollAdvanceInput!) {
    createPayrollAdvance(input: $input) {
      advanceId
      userId
      amountCents
      periodEndMs
      createdByUserId
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_PAYROLL_ADVANCE = gql`
  mutation UpdatePayrollAdvance($advanceId: ID!, $amountCents: String!, $periodEndMs: String!) {
    updatePayrollAdvance(advanceId: $advanceId, amountCents: $amountCents, periodEndMs: $periodEndMs) {
      advanceId
      userId
      amountCents
      periodEndMs
      createdByUserId
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_PAYROLL_ADVANCE = gql`
  mutation DeletePayrollAdvance($advanceId: ID!) {
    deletePayrollAdvance(advanceId: $advanceId)
  }
`;

// ── Payroll Snapshot History (2026-06-09) ──────────────────────────────
export const ISSUE_PAYROLL_SNAPSHOT = gql`
  mutation IssuePayrollSnapshot($refMs: String!, $confirmRecapture: Boolean) {
    issuePayrollSnapshot(refMs: $refMs, confirmRecapture: $confirmRecapture) {
      id
      periodEndMs
      source
      unreviewed
    }
  }
`;

export const RECONCILE_PAYROLL_SNAPSHOT = gql`
  mutation ReconcilePayrollSnapshot($periodEndMs: String!, $reason: String) {
    reconcilePayrollSnapshot(periodEndMs: $periodEndMs, reason: $reason) {
      id
      periodEndMs
      reconciliations {
        reconciledAtMs
      }
    }
  }
`;

// Resend paystub emails for a period (2026-07-31, admin-only) — the
// correction/bounce lever. Recipients are always each user's own email.
export const RESEND_PAYSTUB_EMAILS = gql`
  mutation ResendPaystubEmails($periodEndMs: String!, $userIds: [String!]) {
    resendPaystubEmails(periodEndMs: $periodEndMs, userIds: $userIds) {
      sent
      skipped
      failed
      redirected
      held
    }
  }
`;

// Manual YTD adjustments (Phase 8), admin-only.
export const CREATE_PAYROLL_YTD_ADJUSTMENT = gql`
  mutation CreatePayrollYtdAdjustment($userId: String!, $year: String!, $amountCents: String!, $note: String!) {
    createPayrollYtdAdjustment(userId: $userId, year: $year, amountCents: $amountCents, note: $note) {
      id
      userId
      amountCents
      note
    }
  }
`;

export const UPDATE_PAYROLL_YTD_ADJUSTMENT = gql`
  mutation UpdatePayrollYtdAdjustment($id: ID!, $amountCents: String!, $note: String!) {
    updatePayrollYtdAdjustment(id: $id, amountCents: $amountCents, note: $note) {
      id
      amountCents
      note
    }
  }
`;

export const DELETE_PAYROLL_YTD_ADJUSTMENT = gql`
  mutation DeletePayrollYtdAdjustment($id: ID!) {
    deletePayrollYtdAdjustment(id: $id)
  }
`;
// Door classification review tools (door-gallery classification, 2026-06-11).
export const CONFIRM_BAGROUP_CLASSIFICATION = gql`
  mutation ConfirmBAGroupClassification(
    $bagroupId: String!
    $input: ClassificationConfirmInput!
  ) {
    confirmBAGroupClassification(bagroupId: $bagroupId, input: $input) {
      _id
      hasBeenClassificationPassed
      classification {
        status
        doorStyleId
      }
    }
  }
`;

export const CREATE_DOOR_STYLE = gql`
  mutation CreateDoorStyle($name: String!, $definition: String!) {
    createDoorStyle(name: $name, definition: $definition) {
      _id
      name
      definition
    }
  }
`;

export const SET_CLASSIFICATION_WORKER_ENABLED = gql`
  mutation SetClassificationWorkerEnabled($enabled: Boolean!) {
    setClassificationWorkerEnabled(enabled: $enabled)
  }
`;

export const REOPEN_BAGROUP_CLASSIFICATIONS = gql`
  mutation ReopenBAGroupClassifications($bagroupIds: [String!]!) {
    reopenBAGroupClassifications(bagroupIds: $bagroupIds)
  }
`;

// Software Expenses manual refresh (2026-09-04) — triggers the vendor pull
// now; server-throttled (doc updatedAtMs < 10min => stored doc returned
// untouched, so compare updatedAtMs to tell "refreshed" from "already fresh").
export const REFRESH_SOFTWARE_EXPENSES = gql`
  mutation RefreshSoftwareExpenses {
    refreshSoftwareExpenses {
      month
      vendors {
        vendor
        mtdCents
        projectedCents
        basis
        fetchedAtMs
        error
      }
      totalMtdCents
      totalProjectedCents
      updatedAtMs
      twilioTrackedBaseCents
    }
  }
`;

// Presence L1-3 (office comms, 2026-09-08) — thread-viewer presence ops,
// comms family. join/heartbeat RETURN the full authoritative roster so chip
// accuracy never depends on push latency; ghostSocketEnabled reflects the
// server's BDR_GHOST_TYPING flag (clients never attempt /ws/thread-draft
// unless it is true). Callers must use fetchPolicy: "no-cache" (F11).
export const JOIN_SMS_THREAD = gql`
  mutation JoinSmsThread($phoneNumId: String!, $deviceId: String!) {
    joinSmsThread(phoneNumId: $phoneNumId, deviceId: $deviceId) {
      rosterV
      ghostSocketEnabled
      viewers {
        deviceId
        userId
        userName
        typing
      }
    }
  }
`;

export const HEARTBEAT_SMS_THREAD = gql`
  mutation HeartbeatSmsThread(
    $phoneNumId: String!
    $deviceId: String!
    $typing: Boolean
  ) {
    heartbeatSmsThread(
      phoneNumId: $phoneNumId
      deviceId: $deviceId
      typing: $typing
    ) {
      rosterV
      ghostSocketEnabled
      viewers {
        deviceId
        userId
        userName
        typing
      }
    }
  }
`;

export const LEAVE_SMS_THREAD = gql`
  mutation LeaveSmsThread($phoneNumId: String!, $deviceId: String!) {
    leaveSmsThread(phoneNumId: $phoneNumId, deviceId: $deviceId)
  }
`;

// ── Receipts (inventory hardening Phase 3, plan §3.6) ─────────────────────
// No createReceipt on the web (D10 — capture is mobile-only). Every edit
// returns the whole receipt with derived fields recomputed server-side;
// confirmReceipt IS the receive (one transaction, idempotent on re-confirm).
export const UPDATE_RECEIPT_LINES = gql`
  mutation UpdateReceiptLines($receiptId: ID!, $edits: [ReceiptLineEditInput!]!) {
    updateReceiptLines(receiptId: $receiptId, edits: $edits) {
      ${RECEIPT_FIELDS}
    }
  }
`;

export const UPDATE_RECEIPT_HEADER = gql`
  mutation UpdateReceiptHeader($receiptId: ID!, $patch: ReceiptHeaderInput!) {
    updateReceiptHeader(receiptId: $receiptId, patch: $patch) {
      ${RECEIPT_FIELDS}
    }
  }
`;

export const CONFIRM_RECEIPT = gql`
  mutation ConfirmReceipt($receiptId: ID!) {
    confirmReceipt(receiptId: $receiptId) {
      receipt {
        ${RECEIPT_FIELDS}
      }
      alreadyApplied
      warnings {
        code
        itemId
        itemName
      }
    }
  }
`;

export const DISCARD_RECEIPT = gql`
  mutation DiscardReceipt($receiptId: ID!) {
    discardReceipt(receiptId: $receiptId) {
      id
      status
      discardedAt
    }
  }
`;

export const RETRY_RECEIPT_EXTRACTION = gql`
  mutation RetryReceiptExtraction($receiptId: ID!) {
    retryReceiptExtraction(receiptId: $receiptId) {
      ${RECEIPT_FIELDS}
    }
  }
`;

export const REMATCH_RECEIPT = gql`
  mutation RematchReceipt($receiptId: ID!) {
    rematchReceipt(receiptId: $receiptId) {
      ${RECEIPT_FIELDS}
    }
  }
`;

// ── Door margin (inventory hardening Phase 4, plan §3.7) ─────────────────
export const SAVE_DOOR_MATERIAL_RECIPE = gql`
  mutation SaveDoorMaterialRecipe($jobKind: String!, $servicesItemId: String, $lines: [DoorRecipeLineInput!]!, $note: String) {
    saveDoorMaterialRecipe(jobKind: $jobKind, servicesItemId: $servicesItemId, lines: $lines, note: $note) {
      id
      jobKind
      version
      servicesItemId
      servicesItemName
      lines {
        itemId
        itemName
        qtyPerDoor
      }
      effectiveFrom
      createdByName
      note
    }
  }
`;

export const SAVE_OPERATING_EXPENSE = gql`
  mutation SaveOperatingExpense($input: OperatingExpenseInput!) {
    saveOperatingExpense(input: $input) {
      id
      category
      sourceOptionId
      sourceName
      amountCents
      periodStart
      periodEnd
      note
      dateCreated
    }
  }
`;

export const DELETE_OPERATING_EXPENSE = gql`
  mutation DeleteOperatingExpense($id: ID!) {
    deleteOperatingExpense(id: $id)
  }
`;

export const SET_REFERRAL_SOURCE_COST = gql`
  mutation SetReferralSourceCost($sourceOptionId: String!, $monthlyCents: String!, $effectiveFrom: String!) {
    setReferralSourceCost(sourceOptionId: $sourceOptionId, monthlyCents: $monthlyCents, effectiveFrom: $effectiveFrom) {
      _id
      source
      costHistory {
        monthlyCents
        effectiveFrom
        setBy
        setAt
      }
    }
  }
`;

export const UPDATE_SERVICES_ITEM_DOOR_EQUIVALENT = gql`
  mutation UpdateServicesItemDoorEquivalent($servicesItemId: String!, $doorEquivalent: String!) {
    updateServicesItemDoorEquivalent(servicesItemId: $servicesItemId, doorEquivalent: $doorEquivalent) {
      id
      doorEquivalent
    }
  }
`;

export const COMPUTE_DOOR_COST_SNAPSHOT = gql`
  mutation ComputeDoorCostSnapshot {
    computeDoorCostSnapshot {
      ${DOOR_COST_SNAPSHOT_FIELDS}
    }
  }
`;

export const SET_INVENTORY_FLAGS = gql`
  mutation SetInventoryFlags($inventoryReminderEnabled: Boolean, $doorCostSnapshotEnabled: Boolean) {
    setInventoryFlags(inventoryReminderEnabled: $inventoryReminderEnabled, doorCostSnapshotEnabled: $doorCostSnapshotEnabled) {
      inventoryReminderEnabled
      doorCostSnapshotEnabled
    }
  }
`;

// QR receive, or a tech RETURN when returnFromUserId is set (stock up AND that
// tech's usage corrected). Web uses it from the Adjust dialog: "Return from a
// tech" and the adjust-up guard's "Record as Receive instead". idempotencyKey
// is minted once per dialog per mode and never shared with the adjust key —
// the server dedupes on the key alone and returns silently.
export const RECEIVE_INVENTORY_ITEM = gql`
  mutation ReceiveInventoryItem($itemId: String!, $quantityToAdd: String!, $idempotencyKey: String!, $returnFromUserId: String, $clientReportedAt: String) {
    receiveInventoryItem(itemId: $itemId, quantityToAdd: $quantityToAdd, idempotencyKey: $idempotencyKey, returnFromUserId: $returnFromUserId, clientReportedAt: $clientReportedAt) {
      id
      itemName
      currentQuantity
    }
  }
`;

/**
 * SET_CALENDAR_ITEM_AVAILABLE_SOONER — the "Avail. Sooner" checkbox.
 *
 * Its own mutation rather than a field on UPDATE_CALENDAR_ITEM: that resolver
 * carries chain, placeholder and doc-attach side effects a checkbox must not
 * risk. The server refuses any eventType other than job / go_by.
 */
export const SET_CALENDAR_ITEM_AVAILABLE_SOONER = gql`
  mutation SetCalendarItemAvailableSooner(
    $itemId: ID!
    $availableSooner: Boolean!
  ) {
    setCalendarItemAvailableSooner(
      itemId: $itemId
      availableSooner: $availableSooner
    ) {
      id
      availableSooner
    }
  }
`;

/** Write the signed-in user's note. Returns the stored value (null = cleared). */
export const SET_MY_TASK_NOTE = gql`
  mutation SetMyTaskNote($note: String) {
    setMyTaskNote(note: $note)
  }
`;
